import React, { useState } from 'react'
import ExpandablePanel from 'components/ExpandablePanel'
import styled, { css } from 'styled-components'
import { useDispatch } from 'react-redux'
import Input from 'components/Input'
import Select from 'components/Select'
import Button from 'components/Button'
import { isNilOrEmpty, isNotNilOrEmpty } from 'utils/ramda'
import StudentTeachersList from 'modules/students/components/StudentTeachersList'
import AddStudentTeacherModal from 'modules/students/components/AddStudentTeacherModal'
import { addStudent } from 'services/StudentsService'
import { getActiveStudentsRoutine } from 'modules/students/ducks/actions'
import { update } from 'ramda'
import moment from 'moment'
import { DATE_FORMATS } from 'utils/date'
import { STUDENT_ACCOUNTS } from 'utils/student'
import { updateTeacherAddedStudents } from 'services/TeachersService'

const emptyValues = {
  name: '',
  teachers: [],
  email: '',
  phoneNumber: '',
  comment: '',
  availability: '',
  archived: false,
  removed: false,
  balance: 0,
  account: 'account1',
  plannedSubjects: ''
}

const AddStudentForm = ({
  callback,
  skipAccount = false,
  title = 'Nowy uczeń',
  showAvailability = false,
  hideTeacherAssignment = false,
  showPlannedSubjects = false,
  isNewStudentsView = false
}) => {
  const [values, setValues] = useState(emptyValues)
  const dispatch = useDispatch()

  const handleValueChange = (name, value) => {
    setValues(prev => ({ ...prev, [name]: value }))
  }

  const handleSubmit = e => {
    e.preventDefault()
    const hasTeachers = isNotNilOrEmpty(values.teachers)
    const comment = (values.comment || '').trim()
    const payload = {
      ...values,
      name: values.name.trim(),
      email: showPlannedSubjects ? '' : String(values.email || '').trim(),
      phoneNumber: values.phoneNumber.trim(),
      availability: showAvailability ? (values.availability || '').trim() : '',
      createdAt: moment().format(DATE_FORMATS.withTimeAndSeconds),
      isNew: !hasTeachers,
      account: skipAccount ? '' : values.account
    }
    if (comment) payload.comment = comment
    if (showPlannedSubjects) {
      const planned = String(values.plannedSubjects || '').trim()
      if (planned) payload.plannedSubjects = planned
      else delete payload.plannedSubjects
    } else {
      delete payload.plannedSubjects
    }
    addStudent(payload, () => {
      typeof callback === 'function'
        ? callback()
        : dispatch(getActiveStudentsRoutine())
      setValues(emptyValues)
    })

    const valuesTeachers = values.teachers?.map(teacher => teacher.id) || []
    const teachersToUpdate = Array.from(new Set(valuesTeachers))

    teachersToUpdate.forEach(teacher => {
      updateTeacherAddedStudents({
        teacherId: teacher,
        student: values
      })
    })
  }

  const handleAddTeacher = teacherValues => {
    handleValueChange('teachers', [
      ...values.teachers,
      teacherValues
    ])
  }

  const handleEditTeacher = (teacherValues, index) => {
    setValues(prev => ({
      ...prev,
      teachers: update(index, teacherValues, prev.teachers)
    }))
  }

  const handleDeleteTeacher = index => {
    setValues(prev => ({
      ...prev,
      teachers: values.teachers.filter((_, i) => i !== index)
    }))
  }

  return (
    <ExpandablePanel title={title}>
      <SectionHeader>Dane podstawowe</SectionHeader>
      <Form onSubmit={handleSubmit}>
        <Row>
          <Input
            label='Nazwa ucznia'
            name='name'
            onChange={handleValueChange}
            value={values.name}
          />
          {showPlannedSubjects ? (
            <Input
              label='Planowane przedmioty'
              name='plannedSubjects'
              onChange={handleValueChange}
              value={values.plannedSubjects}
              placeholder='np. matematyka, angielski'
            />
          ) : (
            <Input
              label='Email'
              name='email'
              onChange={(name, value) => handleValueChange('email', value.replace(/\s/g, ''))}
              value={values.email}
            />
          )}
          <Input
            label='Numer telefonu'
            name='phoneNumber'
            onChange={handleValueChange}
            value={values.phoneNumber}
          />
        </Row>
        <Row $alignStart>
          <Input
            label='Komentarz'
            name='comment'
            onChange={handleValueChange}
            value={values.comment}
            placeholder='Notatki o uczniu...'
            multiline
            rows={8}
          />
          {showAvailability && (
            <Input
              label='Dyspozycyjność'
              name='availability'
              onChange={handleValueChange}
              value={values.availability}
              placeholder='np. pon.-pt. 16-20'
              multiline
              rows={8}
            />
          )}
        </Row>
        {!skipAccount && (
          <Row>
            <Select
              label='Konto do wpłat'
              name='account'
              onChange={handleValueChange}
              value={values.account}
              options={Object.entries(STUDENT_ACCOUNTS).map(([value, label]) => ({ value, label }))}
            />
          </Row>
        )}
        {!hideTeacherAssignment && (
          <>
            <ListHeaderWrapper>
              <SectionHeader noMargin>Przypisani nauczyciele</SectionHeader>
              <AddStudentTeacherModal onAddTeacher={handleAddTeacher} />
            </ListHeaderWrapper>
            {isNotNilOrEmpty(values.teachers) && (
              <StudentTeachersList
                onEditTeacher={handleEditTeacher}
                onDeleteTeacher={handleDeleteTeacher}
                teachers={values.teachers}
              />
            )}
          </>
        )}
        <ButtonWrapper>
          <Button
            type='submit'
            color='primary-outline'
            disabled={isNilOrEmpty(values.name)}
          >
            {isNewStudentsView ? 'Stwórz fioleta' : 'Stwórz ucznia'}
          </Button>
        </ButtonWrapper>
      </Form>
    </ExpandablePanel>
  )
}

export default AddStudentForm

const Form = styled.form`
  margin-top: 10px;
  width: 100%;
  
  input {
    width: 100%;
  }
`

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 30px;

  button {
    max-width: 200px;
  }
`

const Row = styled.div`
  display: flex;
  width: 100%;
  gap: 20px;
  align-items: ${({ $alignStart }) => ($alignStart ? 'flex-start' : 'center')};
  
  ${({ inModal }) => inModal && css`
    flex-direction: column;
    & > div {
      width: 100%;
    }
  `}
`

const SectionHeader = styled.div`
  font-size: 14px;
  text-transform: uppercase;
  margin-top: ${({ noMargin }) => noMargin ? '0' : '30px'};
  color: ${({ theme }) => theme.colors.secondary.main};
`

const ListHeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 30px 0 10px;
`
